import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Clapperboard } from "lucide-react";

import ViewContainer from "../../../../components/layout/view/ViewContainer";
import ContentPanel from "../../../../components/layout/content/ContentPanel";
import ContentPanelHeader from "../../../../components/layout/content/ContentPanelHeader";

import Text from "../../../../components/typography/Text";
import ContentGrid from "../../../../components/grids/content/ContentGrid";
import EmptyState from "../../../../components/feedback/EmptyState";
import Button from "../../../../components/buttons/Button";

import VideoThumbnailCard from "../../../../components/cards/thumbnails/VideoThumbnailCard";

import { useHomeVideos } from "../hooks/useHomeVideos";

import "./HomeVideosView.css";

const LIMIT = 8;

export default function HomeVideosView() {
  const navigate = useNavigate();

  const { videos, loading, error, refresh } = useHomeVideos({
    limit: LIMIT,
  });

  const videoItems = useMemo(() => {
    if (!Array.isArray(videos)) return [];

    const seen = new Set();

    return videos
      .filter((video) => {
        const videoId = video?.videoId ?? video?.videoTag;

        if (videoId == null || videoId === "") return false;

        const key = String(videoId);

        if (seen.has(key)) return false;

        seen.add(key);
        return true;
      })
      .slice(0, LIMIT);
  }, [videos]);

  const hasVideos = videoItems.length > 0;

  const handleOpenVideo = (video) => {
    const videoTag = String(video?.videoTag || "").trim();

    if (!videoTag) return;

    const hlsStatus = String(video?.hlsStatus || "")
      .trim()
      .toLowerCase();

    if (["processing", "pending"].includes(hlsStatus)) return;

    navigate(`/watch/${encodeURIComponent(videoTag)}`);
  };

  const renderVideo = (video) => {
    const hlsStatus = String(video?.hlsStatus || "")
      .trim()
      .toLowerCase();

    const isProcessing = ["processing", "pending"].includes(hlsStatus);

    return (
      <VideoThumbnailCard
        key={video.videoId ?? video.videoTag}
        video={video}
        disabled={isProcessing}
        onClick={() => handleOpenVideo(video)}
        showPlayButton
      />
    );
  };

  return (
    <ViewContainer>
      <ContentPanel>
        <ContentPanelHeader title="Videos">
          <Link to="/katchrugby?tab=videos" className="home-videos__view-all">
            View all
          </Link>
        </ContentPanelHeader>

        {loading && !hasVideos ? (
          <ContentGrid
            items={[]}
            loading
            columns={4}
            tabletColumns={3}
            mobileColumns={2}
            gap={16}
            skeletonCount={LIMIT}
          />
        ) : !hasVideos ? (
          <EmptyState
            icon={<Clapperboard size={28} />}
            title={error ? "Unable to load videos" : "No videos available"}
            description={
              error || "There are no public videos available right now."
            }
            action={
              error ? (
                <Button
                  value="Try Again"
                  variant="primary-outlined"
                  onClick={refresh}
                />
              ) : null
            }
          />
        ) : (
          <>
            {error && (
              <div className="home-videos__error" role="alert">
                <Text as="p" size="sm">
                  {error}
                </Text>
              </div>
            )}

            <ContentGrid
              items={videoItems}
              columns={4}
              tabletColumns={3}
              mobileColumns={2}
              gap={16}
              showEmpty={false}
              renderItem={renderVideo}
            />
          </>
        )}
      </ContentPanel>
    </ViewContainer>
  );
}
